<script setup lang="ts">
/**
 * UI-kit component
 * Category: Other
 * Name: DropZone
 */
import type {
  DropZoneProps,
  DropZoneAcceptedFile,
  DropZoneRejectedItem,
  DropZoneItemPayload,
  DropZoneDisplayItem,
} from '../types/interfaces'

import { useDropZone } from '../composables/use-drop-zone.hook'
import DropZoneArea from './DropZoneArea.vue'
import DropZoneList from './DropZoneList.vue'
import DropZoneItem from './DropZoneItem.vue'
import DropZoneItemRejected from './DropZoneItemRejected.vue'

defineOptions({
  name: 'DropZone',
})

const props = withDefaults(defineProps<DropZoneProps>(), {
  uploads: undefined,
  accept: undefined,
  maxSize: undefined,
  maxFiles: undefined,
  multiple: true,
  disabled: false,
  title: 'Drag & Drop your files here',
  browseText: 'Browse files',
  uploadedTitle: 'Uploaded files',
  error: undefined,
})

const emit = defineEmits<{
  accept: [files: DropZoneAcceptedFile[]]
  reject: [items: DropZoneRejectedItem[]]
  remove: [payload: DropZoneItemPayload]
  retry: [payload: DropZoneItemPayload]
}>()

defineSlots<{
  area?: (scope: { addFiles: (files: File[]) => void, disabled: boolean }) => unknown
  item?: (scope: { item: DropZoneDisplayItem, remove: () => void, retry: () => void }) => unknown
  'rejected-item'?: (scope: { item: DropZoneRejectedItem }) => unknown
}>()

const {
  displayItems,
  rejectedItems,
  hasAny,
  addFiles,
  removeItem,
  retryItem,
  clear,
} = useDropZone({
  props,
  onAccept: files => emit('accept', files),
  onReject: items => emit('reject', items),
  onRemove: payload => emit('remove', payload),
  onRetry: payload => emit('retry', payload),
})

defineExpose({
  clear,
})
</script>

<template>
  <div class="drop-zone">
    <slot
      name="area"
      :add-files="addFiles"
      :disabled="disabled"
    >
      <DropZoneArea
        :disabled="disabled"
        :title="title"
        :browse-text="browseText"
        :accept="accept"
        :multiple="multiple"
        @files="addFiles"
      />
    </slot>

    <DropZoneList
      v-if="hasAny || error"
      :title="uploadedTitle"
      :error="error"
    >
      <template
        v-for="item in displayItems"
        :key="item.id"
      >
        <slot
          name="item"
          :item="item"
          :remove="() => removeItem(item.id)"
          :retry="() => retryItem(item.id)"
        >
          <DropZoneItem
            :name="item.name"
            :size="item.size"
            :status="item.status"
            :progress="item.progress"
            :view-link="item.viewLink"
            :download-link="item.downloadLink"
            :disabled="disabled"
            :error="item.error"
            @remove="removeItem(item.id)"
            @retry="retryItem(item.id)"
          />
        </slot>
      </template>

      <template
        v-for="rejected in rejectedItems"
        :key="rejected.id"
      >
        <slot
          name="rejected-item"
          :item="rejected"
        >
          <DropZoneItemRejected
            :name="rejected.name"
            :size="rejected.size"
            :reason="rejected.reason"
          />
        </slot>
      </template>
    </DropZoneList>
  </div>
</template>

<style lang="scss" scoped src="../styles/_drop-zone.scss" />
